/**
 * React Query hooks for admin error management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/apiClient';
import { adminErrorKeys } from './queries/adminErrorKeys';
import { toast } from '@/hooks/use-toast';

// Types
export interface ErrorLog {
  id: string;
  error_type: string;
  severity: string;
  message: string;
  stack_trace?: string;
  error_code?: string;
  user_id?: string;
  user_email?: string;
  request_path?: string;
  request_method?: string;
  user_agent?: string;
  ip_address?: string;
  browser?: string;
  os?: string;
  environment?: Record<string, any>;
  metadata?: Record<string, any>;
  error_hash?: string;
  occurrence_count: number;
  first_seen: string;
  last_seen: string;
  resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  resolution_notes?: string;
  created_at: string;
}

export interface ErrorLogListResponse {
  errors: ErrorLog[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface ErrorStats {
  total_errors: number;
  unresolved_errors: number;
  errors_last_24h: number;
  errors_last_7d: number;
  critical_errors: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  top_errors: Array<{
    id: string;
    message: string;
    error_code?: string;
    occurrence_count: number;
    severity: string;
    last_seen: string;
  }>;
}

export interface ErrorFilters {
  page?: number;
  page_size?: number;
  error_type?: string;
  severity?: string;
  resolved?: boolean;
  search?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Fetch error logs with filtering and pagination
 */
export function useErrorLogs(filters: ErrorFilters = {}) {
  return useQuery({
    queryKey: adminErrorKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });

      const response = await apiClient.get<ErrorLogListResponse>(
        `/v1/admin/errors?${params.toString()}`
      );
      return response;
    },
  });
}

/**
 * Fetch error statistics
 */
export function useErrorStats() {
  return useQuery({
    queryKey: adminErrorKeys.stats(),
    queryFn: async () => {
      return await apiClient.get<ErrorStats>('/v1/admin/errors/stats');
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

/**
 * Fetch single error by ID
 */
export function useErrorById(errorId: string | undefined) {
  return useQuery({
    queryKey: adminErrorKeys.detail(errorId!),
    queryFn: async () => {
      return await apiClient.get<ErrorLog>(`/v1/admin/errors/${errorId}`);
    },
    enabled: !!errorId,
  });
}

/**
 * Resolve an error
 */
export function useResolveError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      errorId,
      resolution_notes,
    }: {
      errorId: string;
      resolution_notes?: string;
    }) => {
      return await apiClient.post(`/v1/admin/errors/${errorId}/resolve`, {
        resolution_notes,
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.stats() });
      queryClient.invalidateQueries({
        queryKey: adminErrorKeys.detail(variables.errorId),
      });
      toast({
        title: 'Success',
        description: 'Error marked as resolved',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to resolve error',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Unresolve an error
 */
export function useUnresolveError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (errorId: string) => {
      return await apiClient.post(`/v1/admin/errors/${errorId}/unresolve`);
    },
    onSuccess: (_, errorId) => {
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.stats() });
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.detail(errorId) });
      toast({
        title: 'Success',
        description: 'Error marked as unresolved',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unresolve error',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Delete an error
 */
export function useDeleteError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (errorId: string) => {
      return await apiClient.delete(`/v1/admin/errors/${errorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminErrorKeys.stats() });
      toast({
        title: 'Success',
        description: 'Error log deleted',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete error',
        variant: 'destructive',
      });
    },
  });
}
